/**
 * Conversion de Markdown a HTML y a nodos ricos.
 *
 * Escenario real: la autora copia un articulo generado en ChatGPT, pegado
 * desde un archivo .md o copiado de un editor que no ofrece formato rico, y
 * lo pega en el editor del blog. El portapapeles trae el Markdown tal cual —
 * `## Titulo`, `**negrita**`, `- elemento` — y sin esta capa esos caracteres
 * quedarian visibles en el articulo publicado.
 *
 * El modulo es TypeScript puro, sin DOM: se ejecuta igual en el navegador
 * (durante el pegado) y en Node (durante los tests). Todo texto se escapa con
 * `escapeHtml` y todas las URLs pasan por `safeUrl`, de modo que el HTML que
 * produce aqui vuelve a ser saneado por el editor antes de insertarse: doble
 * defensa contra inyeccion.
 *
 * No es un parser de Markdown completo ni pretende serlo. Cubre el subconjunto
 * que el editor soporta (H2/H3, listas, enlaces, citas, separadores, tablas,
 * codigo) y ante cualquier otra cosa deja texto plano, que es siempre un
 * resultado seguro.
 */

import { escapeHtml, normalizeNewlines, safeUrl } from "./text";
import type { RichMark, RichNode } from "./types";

const HEADING_PATTERN = /^(#{1,6})\s+(.*)$/;
const HR_PATTERN = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const BULLET_ITEM_PATTERN = /^\s*[-*+]\s+\S/;
const ORDERED_ITEM_PATTERN = /^\s*\d+[.)]\s+\S/;
const QUOTE_PATTERN = /^\s*>/;
const FENCE_PATTERN = /^\s*```/;
const TABLE_ROW_PATTERN = /^\|.*\|/;

/**
 * Senales de que el texto plano trae Markdown: encabezados, separadores,
 * citas, cercas de codigo, listas, tablas y los marcadores inline. Con una
 * sola senal basta para intentar la conversion; lo que no se reconozca
 * queda como texto.
 */
export function looksLikeMarkdown(value: string): boolean {
  const lines = normalizeNewlines(value).split("\n");
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (HEADING_PATTERN.test(line)) return true;
    if (HR_PATTERN.test(line)) return true;
    if (QUOTE_PATTERN.test(line)) return true;
    if (FENCE_PATTERN.test(line)) return true;
    if (BULLET_ITEM_PATTERN.test(line)) return true;
    if (ORDERED_ITEM_PATTERN.test(line)) return true;
    if (TABLE_ROW_PATTERN.test(line) && line.endsWith("|")) return true;
  }
  if (/\*\*[^*\n]+\*\*/.test(value)) return true;
  if (/~~[^~\n]+~~/.test(value)) return true;
  if (/\[[^\]\n]+\]\([^)\s]+\)/.test(value)) return true;
  return false;
}

/* ------------------------------------------------------------------ */
/* Marcas inline                                                       */
/* ------------------------------------------------------------------ */

interface InlineSegment {
  text: string;
  marks: RichMark[];
  /** Imagen Markdown: se serializa como <img> o degrada a su texto alternativo. */
  image?: { src: string; alt: string };
}

/**
 * Divide segmentos aplicando un patron. Los segmentos con marca `code` no se
 * tocan: el contenido literal entre acentos graves nunca se interpreta.
 */
function applyPattern(
  segments: InlineSegment[],
  pattern: RegExp,
  build: (match: RegExpExecArray) => { text: string; marks: RichMark[]; image?: { src: string; alt: string } },
): InlineSegment[] {
  const out: InlineSegment[] = [];
  for (const segment of segments) {
    if (segment.image || segment.marks.some((mark) => mark.type === "code")) {
      out.push(segment);
      continue;
    }
    let last = 0;
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(segment.text)) !== null) {
      if (match[0].length === 0) {
        pattern.lastIndex += 1;
        continue;
      }
      if (match.index > last) out.push({ text: segment.text.slice(last, match.index), marks: segment.marks });
      const built = build(match);
      const next: InlineSegment = { text: built.text, marks: [...segment.marks, ...built.marks] };
      if (built.image) next.image = built.image;
      out.push(next);
      last = match.index + match[0].length;
    }
    if (last < segment.text.length) out.push({ text: segment.text.slice(last), marks: segment.marks });
  }
  return out;
}

function tokenizeInline(value: string): InlineSegment[] {
  let segments: InlineSegment[] = [{ text: value, marks: [] }];

  // Codigo literal primero: lo demas no debe interpretarse dentro.
  segments = applyPattern(segments, /`([^`\n]+)`/g, (match) => ({
    text: match[1] ?? "",
    marks: [{ type: "code" }],
  }));

  // Imagenes antes que enlaces: `![alt](src)` contiene la forma `[alt](src)`.
  // El destino admite parentesis balanceados: `javascript:alert(1)` debe
  // llegar entero a safeUrl para ser rechazado como tal.
  segments = applyPattern(segments, /!\[([^\]]*)\]\(([^()\s]*(?:\([^()]*\)[^()\s]*)*)\)/g, (match) => {
    const alt = match[1] ?? "";
    const src = safeUrl(match[2] ?? "");
    if (!src) return { text: alt, marks: [] };
    return { text: alt, marks: [], image: { src, alt } };
  });

  segments = applyPattern(segments, /\[([^\]]*)\]\(([^()\s]*(?:\([^()]*\)[^()\s]*)*)\)/g, (match) => {
    const label = match[1] ?? "";
    const href = safeUrl(match[2] ?? "");
    // URL no permitida: se conserva el texto del enlace, nunca el destino.
    if (!href) return { text: label, marks: [] };
    return { text: label, marks: [{ type: "link", attrs: { href } }] };
  });

  segments = applyPattern(segments, /\*\*(.+?)\*\*/g, (match) => ({
    text: match[1] ?? "",
    marks: [{ type: "bold" }],
  }));

  segments = applyPattern(segments, /\*([^*\n]+)\*/g, (match) => ({
    text: match[1] ?? "",
    marks: [{ type: "italic" }],
  }));

  segments = applyPattern(segments, /_([^_\n]+)_(?!\w)/g, (match) => ({
    text: match[1] ?? "",
    marks: [{ type: "italic" }],
  }));

  segments = applyPattern(segments, /~~([^~\n]+)~~/g, (match) => ({
    text: match[1] ?? "",
    marks: [{ type: "strike" }],
  }));

  return mergeSegments(segments);
}

/**
 * Une segmentos consecutivos con las mismas marcas: al aplicar los patrones
 * uno detras de otro, `**negrita con *cursiva* dentro**` queda partido en
 * tres segmentos con la misma marca `bold`; sin esta fusion el HTML saldria
 * con etiquetas `<strong>` adyacentes en lugar de una sola.
 */
function mergeSegments(segments: InlineSegment[]): InlineSegment[] {
  const out: InlineSegment[] = [];
  for (const segment of segments) {
    const last = out[out.length - 1];
    if (
      last &&
      !last.image &&
      !segment.image &&
      JSON.stringify(last.marks) === JSON.stringify(segment.marks)
    ) {
      out[out.length - 1] = { ...last, text: last.text + segment.text };
      continue;
    }
    out.push({ ...segment });
  }
  return out;
}

function wrapSegment(inner: string, mark: RichMark): string {
  if (mark.type === "bold") return `<strong>${inner}</strong>`;
  if (mark.type === "italic") return `<em>${inner}</em>`;
  if (mark.type === "strike") return `<s>${inner}</s>`;
  if (mark.type === "code") return `<code>${inner}</code>`;
  if (mark.type === "link") return `<a href="${escapeHtml(String(mark.attrs?.href ?? ""))}">${inner}</a>`;
  return inner;
}

function serializeImage(segment: InlineSegment): string {
  const alt = segment.image?.alt ? ` alt="${escapeHtml(segment.image.alt)}"` : "";
  return `<img src="${escapeHtml(segment.image?.src ?? "")}"${alt}>`;
}

function sameMark(a: RichMark | undefined, b: RichMark | undefined): boolean {
  if (!a || !b) return false;
  return a.type === b.type && JSON.stringify(a.attrs ?? {}) === JSON.stringify(b.attrs ?? {});
}

/**
 * Serializa segmentos agrupando rachas que comparten la misma marca exterior.
 * Sin esta agrupacion, `**negrita con *cursiva* dentro**` saldria con tres
 * etiquetas `<strong>` adyacentes en vez de una sola con el `<em>` dentro.
 */
function serializeRuns(segments: InlineSegment[]): string {
  let out = "";
  let index = 0;
  while (index < segments.length) {
    const segment = segments[index]!;
    if (segment.image) {
      out += serializeImage(segment);
      index += 1;
      continue;
    }
    const first = segment.marks[0];
    if (!first) {
      out += escapeHtml(segment.text);
      index += 1;
      continue;
    }
    let end = index + 1;
    while (end < segments.length && !segments[end]?.image && sameMark(segments[end]?.marks[0], first)) end += 1;
    const run = segments.slice(index, end).map((item) => ({ ...item, marks: item.marks.slice(1) }));
    out += wrapSegment(serializeRuns(run), first);
    index = end;
  }
  return out;
}

/** Convierte texto con Markdown inline a HTML escapado. */
export function inlineToHtml(value: string): string {
  return serializeRuns(tokenizeInline(value));
}

/**
 * Convierte texto con Markdown inline a nodos ricos con marcas reales
 * (`bold`, `italic`, `strike`, `code`, `link`). Es lo que usa la
 * reconstruccion desde texto plano para no perder el formato.
 */
export function parseInlineMarkdown(value: string): RichNode[] {
  const nodes: RichNode[] = [];
  for (const segment of tokenizeInline(value)) {
    if (segment.image) {
      // El esquema del editor admite imagenes como nodo de bloque, no inline:
      // en esta via se conserva solo el texto alternativo.
      if (segment.image.alt) nodes.push({ type: "text", text: segment.image.alt });
      continue;
    }
    if (!segment.text) continue;
    const node: RichNode = { type: "text", text: segment.text };
    if (segment.marks.length > 0) node.marks = segment.marks;
    nodes.push(node);
  }
  return nodes;
}

/* ------------------------------------------------------------------ */
/* Bloques                                                             */
/* ------------------------------------------------------------------ */

function headingTag(hashes: string): 2 | 3 {
  // El H1 lo genera el titulo del post; H4 a H6 degradan a H3.
  if (hashes.length <= 2) return 2;
  return 3;
}

function isTableSeparator(line: string): boolean {
  const value = line.trim();
  return /^\|?[\s:|-]+\|?$/.test(value) && value.includes("-");
}

function parseTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

/** Linea que abre un bloque propio y por lo tanto corta el parrafo actual. */
function isBlockStart(line: string): boolean {
  const value = line.trim();
  return (
    HEADING_PATTERN.test(value) ||
    FENCE_PATTERN.test(value) ||
    HR_PATTERN.test(line) ||
    QUOTE_PATTERN.test(value) ||
    BULLET_ITEM_PATTERN.test(value) ||
    ORDERED_ITEM_PATTERN.test(value) ||
    TABLE_ROW_PATTERN.test(value)
  );
}

/**
 * Convierte Markdown a HTML de bloques permitidos por el editor.
 * El resultado se vuelve a pasar por el saneador del editor antes de
 * insertarse, asi que cualquier error aqui queda contenido.
 */
export function markdownToHtml(source: string): string {
  const lines = normalizeNewlines(source).replace(/\r/g, "").split("\n");
  const out: string[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    // Cercas de codigo: contenido literal, solo escapado.
    if (FENCE_PATTERN.test(trimmed)) {
      const buffer: string[] = [];
      index += 1;
      while (index < lines.length && !FENCE_PATTERN.test((lines[index] ?? "").trim())) {
        buffer.push(lines[index] ?? "");
        index += 1;
      }
      index += 1; // cierra la cerca (o termina el texto)
      out.push(`<pre><code>${escapeHtml(buffer.join("\n"))}</code></pre>`);
      continue;
    }

    const heading = HEADING_PATTERN.exec(trimmed);
    if (heading) {
      const tag = headingTag(heading[1] ?? "#");
      const value = (heading[2] ?? "").trim();
      index += 1;
      if (!value) continue;
      out.push(`<h${tag}>${inlineToHtml(value)}</h${tag}>`);
      continue;
    }

    if (HR_PATTERN.test(line)) {
      out.push("<hr>");
      index += 1;
      continue;
    }

    if (QUOTE_PATTERN.test(trimmed)) {
      const buffer: string[] = [];
      while (index < lines.length && QUOTE_PATTERN.test((lines[index] ?? "").trim())) {
        buffer.push((lines[index] ?? "").replace(/^\s*>\s?/, ""));
        index += 1;
      }
      const inner = markdownToHtml(buffer.join("\n"));
      if (inner) out.push(`<blockquote>${inner}</blockquote>`);
      continue;
    }

    if (TABLE_ROW_PATTERN.test(trimmed) && index + 1 < lines.length && isTableSeparator(lines[index + 1] ?? "")) {
      const header = parseTableRow(trimmed);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && TABLE_ROW_PATTERN.test((lines[index] ?? "").trim())) {
        rows.push(parseTableRow(lines[index] ?? ""));
        index += 1;
      }
      const head = `<thead><tr>${header.map((cell) => `<th>${inlineToHtml(cell)}</th>`).join("")}</tr></thead>`;
      const body = `<tbody>${rows
        .map((row) => `<tr>${row.map((cell) => `<td>${inlineToHtml(cell)}</td>`).join("")}</tr>`)
        .join("")}</tbody>`;
      out.push(`<table>${head}${body}</table>`);
      continue;
    }

    if (BULLET_ITEM_PATTERN.test(line)) {
      const items: string[] = [];
      while (index < lines.length && BULLET_ITEM_PATTERN.test(lines[index] ?? "")) {
        items.push((lines[index] ?? "").replace(/^\s*[-*+]\s+/, "").trim());
        index += 1;
      }
      out.push(`<ul>${items.map((item) => `<li>${inlineToHtml(item)}</li>`).join("")}</ul>`);
      continue;
    }

    if (ORDERED_ITEM_PATTERN.test(line)) {
      const items: string[] = [];
      while (index < lines.length && ORDERED_ITEM_PATTERN.test(lines[index] ?? "")) {
        items.push((lines[index] ?? "").replace(/^\s*\d+[.)]\s+/, "").trim());
        index += 1;
      }
      out.push(`<ol>${items.map((item) => `<li>${inlineToHtml(item)}</li>`).join("")}</ol>`);
      continue;
    }

    // Parrafo: lineas consecutivas que no abren bloque.
    const buffer: string[] = [];
    while (index < lines.length) {
      const current = lines[index] ?? "";
      if (!current.trim() || isBlockStart(current)) break;
      buffer.push(current.trim());
      index += 1;
    }
    if (buffer.length > 0) out.push(`<p>${inlineToHtml(buffer.join(" "))}</p>`);
  }

  return out.join("\n");
}
